"use client";

import { SignInButton, SignedIn, SignedOut } from "@clerk/clerk-react";
import { useState } from "react";

import { PACE_OPTIONS, type PaceValue } from "@/lib/constants";

import * as api from "@/src/api/sdk.gen";

type RequestState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; user_id: number }
  | { status: "error"; message: string };

const DEFAULT_PACE: PaceValue = PACE_OPTIONS[0].value;

export default function OnboardingPage() {
  const [goal, setGoal] = useState("");
  const [pace, setPace] = useState<PaceValue>(DEFAULT_PACE);
  const [customPace, setCustomPace] = useState("");
  const [requestState, setRequestState] = useState<RequestState>({
    status: "idle",
  });

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRequestState({ status: "loading" });

    const trimmedGoal = goal.trim();
    const trimmedCustomPace = customPace.trim();
    const finalPace = pace === "custom" ? trimmedCustomPace : pace;

    if (!trimmedGoal || !finalPace) {
      setRequestState({
        status: "error",
        message: "Please provide both goal and pace.",
      });
      return;
    }

    try {
      const { data, error, response } = await api.processUserOnboardingApiV1UsersOnboardingPost({
        body: {
          goal: trimmedGoal,
          paceSelection: pace,
          ...(pace === "custom" ? { customPace: trimmedCustomPace } : {}),
        },
      });

      if (error) {
        const body = error as { error?: string };
        setRequestState({
          status: "error",
          message:
            body.error ??
            `Request failed with status ${response?.status?.toString()}`,
        });
        return;
      }

      const responseData = data as { user_id: number };
      setRequestState({
        status: "success",
        user_id: responseData.user_id,
      });
    } catch (error) {
      console.error("Onboarding request failed", error);
      setRequestState({
        status: "error",
        message: "Unexpected error. See console for details.",
      });
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10 text-slate-900">
      <main className="w-full max-w-xl rounded-2xl bg-white p-8 shadow-2xl">
        <h1 className="text-2xl font-semibold text-slate-900">
          Onboarding
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Sign in to your account, enter a learning goal and pace, then submit to hit{" "}
          .
        </p>

        <SignedOut>
          <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            You need to sign in before saving onboarding details.
            <div className="mt-3">
              <SignInButton mode="modal">
                <button className="rounded-md bg-slate-900 px-4 py-2 text-white">
                  Sign in
                </button>
              </SignInButton>
            </div>
          </div>
        </SignedOut>

        <SignedIn>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="goal"
                className="block text-sm font-medium text-slate-700"
              >
                Learning goal
              </label>
              <textarea
                id="goal"
                value={goal}
                onChange={(event) => setGoal(event.target.value)}
                rows={4}
                className="mt-2 w-full rounded-md border border-slate-300 p-3 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                placeholder="e.g., Become data analyst in 3 months"
              />
            </div>

            <div>
              <label
                htmlFor="pace"
                className="block text-sm font-medium text-slate-700"
              >
                Preferred pace
              </label>
              <select
                id="pace"
                value={pace}
                onChange={(event) => setPace(event.target.value as PaceValue)}
                className="mt-2 w-full rounded-md border border-slate-300 p-3 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
              >
                {PACE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {pace === "custom" ? (
              <div>
                <label
                  htmlFor="customPace"
                  className="block text-sm font-medium text-slate-700"
                >
                  Custom pace
                </label>
                <input
                  id="customPace"
                  type="text"
                  value={customPace}
                  onChange={(event) => setCustomPace(event.target.value)}
                  className="mt-2 w-full rounded-md border border-slate-300 p-3 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  placeholder="e.g., 7_lessons_per_week"
                />
              </div>
            ) : null}

            <button
              type="submit"
              disabled={requestState.status === "loading"}
              className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {requestState.status === "loading"
                ? "Saving..."
                : "Save onboarding"}
            </button>
          </form>

          {requestState.status === "success" ? (
            <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
              Onboarding saved. Welcome to Inara!
            </div>
          ) : null}

          {requestState.status === "error" ? (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {requestState.message}
            </div>
          ) : null}
        </SignedIn>
      </main>
    </div>
  );
}

